import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Trash2,
  Layers,
  Play,
  Pause,
  Pencil,
  X,
  AlertTriangle,
  Search,
  Tag,
  Link,
  Copy,
  Check,
  Hash,
  ExternalLink,
  Wand2,
  Loader2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import type { ComboResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { api } from "@/lib/api";
import { cn, formatPlayCount } from "@/lib/utils";

type ComboSortColumn = "name" | "sounds" | "plays" | "created";
type SortDir = "asc" | "desc";

/* ---------- Confirm Dialog ---------- */

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-[18px] border border-mist bg-slate p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ember/15">
            <AlertTriangle className="h-4 w-4 text-ember" />
          </div>
          <div>
            <h3 className="font-display text-base font-semibold text-parchment">{title}</h3>
            <p className="mt-1 text-sm text-fog">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-xl px-4 py-2 text-sm text-fog hover:bg-slate-raised">
            Cancel
          </button>
          <button onClick={onConfirm} className="rounded-xl bg-ember px-4 py-2 text-sm font-medium text-ink hover:bg-ember/80">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Edit Combo Modal ---------- */

function EditComboModal({
  combo,
  onClose,
  onSaved,
}: {
  combo: ComboResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { playCombo, activeComboId, stopAll } = useAudio();
  const playing = activeComboId === combo.id;
  const [name, setName] = useState(combo.name);
  const [description, setDescription] = useState(combo.description ?? "");
  const [tags, setTags] = useState<string[]>(
    combo.tags.filter((t) => t.slug !== "combo").map((t) => t.name),
  );
  const [tagDraft, setTagDraft] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const handleAiSuggest = async () => {
    setSuggesting(true);
    try {
      const res = await api.admin.combos.aiSuggest({
        name: name.trim() || undefined,
        sounds: combo.sounds.map((s) => ({
          title: s.title,
          transcript: s.transcript ?? undefined,
        })),
      });
      if (res.name && !name.trim()) setName(res.name);
      if (res.description) setDescription(res.description);
      if (res.tags?.length) {
        const merged = [...new Set([...tags, ...res.tags])];
        setTags(merged);
      }
    } catch {}
    setSuggesting(false);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const finalTags = [...tags];
      const leftover = tagDraft.trim();
      if (leftover && !finalTags.includes(leftover.toLowerCase())) {
        finalTags.push(leftover);
      }
      await api.admin.combos.update(combo.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        tags: finalTags,
      });
      onSaved();
    } catch {}
    setSaving(false);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const permalink = `${window.location.origin}/c/${combo.slug}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-2 backdrop-blur-sm sm:py-8 sm:px-4">
      <div className="flex w-full max-w-lg flex-col rounded-[18px] border border-mist bg-slate shadow-xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-4rem)]">
        <div className="flex shrink-0 items-center justify-between border-b border-mist px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-spark/15">
              <Layers className="h-4 w-4 text-spark" />
            </div>
            <h2 className="font-display text-lg font-bold tracking-display text-parchment">Edit Combo</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-fog hover:bg-slate-raised">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {/* Preview player */}
          <div className="flex items-center gap-3 rounded-xl bg-ink/50 p-3">
            <button
              onClick={() => {
                if (playing) { stopAll(); return; }
                playCombo(combo.id, combo.name, combo.sounds.map((s) => ({
                  id: s.id, audioUrl: s.audio_url, title: s.title, durationMs: s.duration_ms, slug: s.slug,
                })));
              }}
              className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors", playing ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft")}
            >
              {playing ? <Pause className="h-4 w-4 text-ink" /> : <Play className="ml-0.5 h-4 w-4 text-ink" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-parchment">{combo.name}</p>
              <div className="flex items-center gap-3 font-mono text-[10px] text-fog">
                <span>{combo.sounds.length} sounds</span>
                <span>{formatPlayCount(combo.play_count)} plays</span>
                <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{combo.slug}</span>
              </div>
            </div>
          </div>

          {/* Sounds list */}
          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              Sound sequence
            </label>
            <div className="space-y-1 rounded-xl border border-mist bg-ink/30 p-2">
              {combo.sounds.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5">
                  <span className="font-mono text-[10px] font-bold text-spark">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-parchment">{s.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional"
              className="w-full resize-none rounded-xl border border-mist bg-ink px-3 py-2 text-sm text-parchment placeholder:text-fog/50 outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
            />
          </div>

          {/* AI Suggest */}
          <div>
            <button
              onClick={handleAiSuggest}
              disabled={suggesting}
              className="flex items-center gap-1.5 rounded-xl bg-spark/10 px-3 py-1.5 text-sm font-medium text-spark transition-colors hover:bg-spark/20 disabled:opacity-50"
            >
              {suggesting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5" />
              )}
              {suggesting ? "Generating…" : "AI suggest description & tags"}
            </button>
          </div>

          {/* Tags — pill input */}
          <div>
            <label className="mb-1.5 block font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <span className="flex items-center gap-1"><Tag className="h-3 w-3" />Tags</span>
            </label>
            <div
              className="flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-xl border border-mist bg-ink px-2 py-1.5 focus-within:border-spark focus-within:ring-2 focus-within:ring-spark/20"
              onClick={() => tagInputRef.current?.focus()}
            >
              {/* Always show the "combo" tag as non-removable */}
              <span className="flex items-center gap-1 rounded-lg bg-fog/15 px-2 py-0.5 text-xs text-fog">
                combo
              </span>
              {tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 rounded-lg bg-spark/15 px-2 py-0.5 text-xs text-spark">
                  {tag}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTags((prev) => prev.filter((t) => t !== tag)); }}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-spark/20"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
              <input
                ref={tagInputRef}
                type="text"
                value={tagDraft}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.includes(",")) {
                    for (const part of val.split(",")) {
                      const trimmed = part.trim().toLowerCase();
                      if (trimmed && trimmed !== "combo" && !tags.includes(trimmed)) {
                        setTags((prev) => [...prev, trimmed]);
                      }
                    }
                    setTagDraft("");
                    return;
                  }
                  setTagDraft(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Tab" || e.key === "Enter") {
                    const trimmed = tagDraft.trim().toLowerCase();
                    if (trimmed && trimmed !== "combo" && !tags.includes(trimmed)) {
                      e.preventDefault();
                      setTags((prev) => [...prev, trimmed]);
                      setTagDraft("");
                    } else if (trimmed) {
                      e.preventDefault();
                      setTagDraft("");
                    }
                  } else if (e.key === "Backspace" && !tagDraft && tags.length > 0) {
                    setTags((prev) => prev.slice(0, -1));
                  }
                }}
                placeholder={tags.length === 0 ? "Type and press comma or tab" : ""}
                className="min-w-[80px] flex-1 bg-transparent px-1 py-0.5 text-sm text-parchment placeholder:text-fog/50 outline-none"
              />
            </div>
          </div>

          {/* Share URLs */}
          <div className="rounded-xl border border-mist bg-ink/50 p-3">
            <p className="mb-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">Share URL</p>
            <button
              onClick={() => handleCopy(permalink, "permalink")}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-mist/50"
            >
              <Link className="h-3.5 w-3.5 shrink-0 text-fog" />
              <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fog">{permalink}</span>
              {copied === "permalink" ? <Check className="h-3.5 w-3.5 shrink-0 text-moss" /> : <Copy className="h-3.5 w-3.5 shrink-0 text-fog" />}
            </button>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-mist px-6 py-4">
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm text-fog hover:bg-slate-raised">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="rounded-xl bg-spark px-5 py-2 text-sm font-medium text-ink hover:bg-spark-soft disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Main Page ---------- */

export function AdminCombos() {
  const { playCombo, activeComboId, stopAll } = useAudio();
  const [combos, setCombos] = useState<ComboResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editingCombo, setEditingCombo] = useState<ComboResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ComboResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [sortCol, setSortCol] = useState<ComboSortColumn>("created");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const toggleSort = useCallback((col: ComboSortColumn) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return col;
      }
      setSortDir(col === "name" ? "asc" : "desc");
      return col;
    });
  }, []);

  useEffect(() => {
    if (searchQuery === "") { setDebouncedSearch(""); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const fetchCombos = useCallback(() => {
    setLoading(true);
    api.admin.combos
      .list({ limit: 500 })
      .then((res) => {
        setCombos(res.combos);
        setTotal(res.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchCombos(); }, [fetchCombos]);

  const filteredCombos = debouncedSearch
    ? combos.filter((c) =>
        c.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        c.sounds.some((s) => s.title.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        c.tags.some((t) => t.name.toLowerCase().includes(debouncedSearch.toLowerCase())),
      )
    : combos;

  const sortedCombos = useMemo(() => {
    const sorted = [...filteredCombos];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case "name": cmp = a.name.localeCompare(b.name); break;
        case "sounds": cmp = a.sounds.length - b.sounds.length; break;
        case "plays": cmp = a.play_count - b.play_count; break;
        case "created": cmp = a.created_at - b.created_at; break;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
    return sorted;
  }, [filteredCombos, sortCol, sortDir]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const selectAll = () => {
    if (selected.size === filteredCombos.length) setSelected(new Set());
    else setSelected(new Set(filteredCombos.map((c) => c.id)));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.admin.combos.delete(deleteTarget.id);
      if (res.ok) fetchCombos();
    } catch {}
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleBulkTag = async (action: "add" | "remove") => {
    const tagNames = bulkTagInput.split(",").map((t) => t.trim()).filter(Boolean);
    if (tagNames.length === 0 || selected.size === 0) return;

    for (const comboId of selected) {
      const combo = combos.find((c) => c.id === comboId);
      if (!combo) continue;

      const existingTags = combo.tags.filter((t) => t.slug !== "combo").map((t) => t.name);
      let newTags: string[];

      if (action === "add") {
        newTags = Array.from(new Set([...existingTags, ...tagNames]));
      } else {
        const removeSet = new Set(tagNames.map((t) => t.toLowerCase()));
        newTags = existingTags.filter((t) => !removeSet.has(t.toLowerCase()));
      }

      await api.admin.combos.update(comboId, { tags: newTags });
    }

    setBulkTagInput("");
    setShowBulkTag(false);
    setSelected(new Set());
    fetchCombos();
  };

  const handleBulkDelete = async () => {
    for (const comboId of selected) {
      await api.admin.combos.delete(comboId);
    }
    setSelected(new Set());
    fetchCombos();
  };

  if (loading && combos.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  return (
    <div className={cn(loading && "pointer-events-none")}>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-display">
          Combos <span className="text-fog">({total})</span>
        </h1>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <button
                onClick={() => setShowBulkTag(!showBulkTag)}
                className="flex items-center gap-1.5 rounded-xl bg-slate-raised px-3 py-1.5 text-sm text-parchment hover:bg-mist"
              >
                <Tag className="h-4 w-4" />
                Bulk tag ({selected.size})
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 rounded-xl bg-ember/10 px-3 py-1.5 text-sm text-ember hover:bg-ember/20"
              >
                <Trash2 className="h-4 w-4" />
                Delete ({selected.size})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="mt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, sound, or tag..."
            className="h-9 w-full rounded-xl border border-mist bg-ink pl-9 pr-8 text-sm text-parchment outline-none placeholder:text-fog/50 focus:border-spark focus:ring-2 focus:ring-spark/20"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-fog hover:text-parchment">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Bulk tag bar */}
      {showBulkTag && (
        <div className="mt-4 flex items-center gap-2 rounded-[18px] border border-mist bg-slate p-3">
          <input
            type="text"
            value={bulkTagInput}
            onChange={(e) => setBulkTagInput(e.target.value)}
            placeholder="Tag names (comma separated)"
            className="flex-1 rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment placeholder:text-fog outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
          />
          <button onClick={() => handleBulkTag("add")} className="rounded-xl bg-spark px-3 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft">
            Add tags
          </button>
          <button onClick={() => handleBulkTag("remove")} className="rounded-xl bg-ember px-3 py-1.5 text-sm font-medium text-ink hover:bg-ember/80">
            Remove tags
          </button>
          <button onClick={() => setShowBulkTag(false)} className="rounded-xl p-1.5 text-fog hover:bg-slate-raised">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Empty state */}
      {filteredCombos.length === 0 ? (
        <div className="mt-12 py-20 text-center text-fog">
          <Layers className="mx-auto h-10 w-10 text-fog/30" />
          <p className="mt-3 font-display text-lg tracking-display">
            {debouncedSearch ? "No combos match your search" : "No combos yet"}
          </p>
          {!debouncedSearch && <p className="mt-1 text-sm">Combos created from the soundboard will appear here</p>}
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-[18px] border border-mist">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-mist bg-slate">
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.size === sortedCombos.length && sortedCombos.length > 0}
                    onChange={selectAll}
                    className="accent-spark"
                  />
                </th>
                <th className="w-10 px-1 py-2" />
                <th className="w-[14%] px-3 py-2 text-left">
                  <button onClick={() => toggleSort("name")} className={cn("flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "name" ? "text-spark" : "text-fog hover:text-parchment")}>
                    Name
                    {sortCol === "name" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                  </button>
                </th>
                <th className="w-[24%] px-3 py-2 text-left">
                  <button onClick={() => toggleSort("sounds")} className={cn("flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "sounds" ? "text-spark" : "text-fog hover:text-parchment")}>
                    Sounds
                    {sortCol === "sounds" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                  </button>
                </th>
                <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-fog">Tags</th>
                <th className="w-16 px-3 py-2 text-right">
                  <button onClick={() => toggleSort("plays")} className={cn("ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors", sortCol === "plays" ? "text-spark" : "text-fog hover:text-parchment")}>
                    Plays
                    {sortCol === "plays" && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                  </button>
                </th>
                <th className="w-28 px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-fog">Actions</th>
              </tr>
            </thead>
            <AnimatePresence mode="popLayout">
              {sortedCombos.map((combo) => {
                const playing = activeComboId === combo.id;
                return (
                  <motion.tr
                    key={combo.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className={cn("border-b border-mist last:border-0", selected.has(combo.id) && "bg-spark/5")}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(combo.id)}
                        onChange={() => toggleSelect(combo.id)}
                        className="accent-spark"
                      />
                    </td>
                    <td className="px-1 py-2">
                      <button
                        onClick={() => {
                          if (playing) { stopAll(); return; }
                          playCombo(combo.id, combo.name, combo.sounds.map((s) => ({
                            id: s.id, audioUrl: s.audio_url, title: s.title, durationMs: s.duration_ms, slug: s.slug,
                          })));
                        }}
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                          playing ? "bg-spark-soft" : "bg-spark hover:bg-spark-soft",
                        )}
                      >
                        {playing
                          ? <Pause className="h-2.5 w-2.5 text-ink" />
                          : <Play className="ml-0.5 h-2.5 w-2.5 text-ink" />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setEditingCombo(combo)}
                        className="block w-full truncate text-left text-parchment hover:text-spark"
                      >
                        {combo.name}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <p className="truncate font-mono text-[10px] text-fog">
                        {combo.sounds.map((s) => s.title).join(" → ")}
                      </p>
                    </td>
                    <td className="overflow-hidden px-3 py-2">
                      <div className="flex flex-wrap gap-1 overflow-hidden" style={{ maxHeight: "3.5rem" }}>
                        {combo.tags.map((tag) => (
                          <span key={tag.id} className="shrink-0 rounded-full bg-slate-raised px-1.5 py-0.5 font-mono text-[10px] text-fog">
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-fog">
                      {formatPlayCount(combo.play_count)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => setEditingCombo(combo)}
                          className="group/btn relative rounded-lg p-1.5 text-fog transition-colors hover:bg-spark/10 hover:text-spark"
                          title="Edit combo"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <a
                          href={`/c/${combo.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg p-1.5 text-fog transition-colors hover:bg-spark/10 hover:text-spark"
                          title="View permalink"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <button
                          onClick={() => setDeleteTarget(combo)}
                          className="group/btn relative rounded-lg p-1.5 text-fog transition-colors hover:bg-ember/10 hover:text-ember"
                          title="Delete combo"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </table>
        </div>
      )}

      {editingCombo && (
        <EditComboModal
          combo={editingCombo}
          onClose={() => setEditingCombo(null)}
          onSaved={() => { setEditingCombo(null); fetchCombos(); }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete combo"
        message={`Permanently delete "${deleteTarget?.name}"? This removes the combo, its play history, and cannot be undone.`}
        confirmLabel={deleting ? "Deleting..." : "Delete"}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
